import { useEffect } from "react";

import {
  getPurchaseEventName,
  pushDataLayerEvent,
  reportGoogleAdsPurchase,
} from "./analytics";
import { getAttribution } from "./attribution";
import { REQUEST_FAILED, syncConversions } from "./services/conversionService";

/**
 * backend と同期し、新しく検知された購入を GA4 / Google 広告タグへ送る
 *
 * この同期には 2 つの役割がある。
 *
 * 1. 広告のクリック識別子を UID に紐づける。以後 backend は、どの端末で決済されても
 *    その識別子を添えて Google 広告へコンバージョンを送れる
 * 2. backend が検知した購入を受け取り、GA4 と Google 広告タグへ送る
 *
 * backend の Google 広告への API 送信も継続する。同じ取引 ID で重複を排除するため、
 * タグ側の送信が失敗しても API で受信済みのデータには影響しない。
 *
 * 通信エラーは次の同期で再試行する。直っていないと何度でも同じ結果になるもの
 * （設定ミス、想定外のデータ）は例外にして、呼び出し側で同期を止める。
 */
const reportDetectedPurchases = async (): Promise<void> => {
  const result = await syncConversions(getAttribution());
  if (result.error === REQUEST_FAILED) {
    return;
  }
  if (!result.ok) {
    throw new Error(`conversion sync failed: ${result.error}`);
  }

  for (const purchase of result.purchases ?? []) {
    pushDataLayerEvent(getPurchaseEventName(purchase.plan), {
      transaction_id: purchase.transactionId,
      value: purchase.value,
      currency: purchase.currency,
      plan: purchase.plan,
    });
    reportGoogleAdsPurchase(
      purchase.transactionId,
      purchase.value,
      purchase.currency,
    );
  }
};

/**
 * ログイン中に backend と同期する
 *
 * 読み込み時と、タブが再び見えたときに 1 回ずつ。決済アプリからブラウザに
 * 戻ってきた場合もここで拾える。決済の検知が遅れても、backend は実際の決済時刻で
 * Google 広告へ送るため、計上される時刻はずれない。
 */
export const useConversionTracking = (uid: string | undefined): void => {
  useEffect(() => {
    if (!uid) {
      return;
    }

    let stopped = false;
    let running = false;

    const run = async () => {
      if (stopped || running || document.visibilityState !== "visible") {
        return;
      }

      running = true;
      try {
        await reportDetectedPurchases();
      } catch (error) {
        console.error("Conversion tracking stopped:", error);
        stopped = true;
      } finally {
        running = false;
      }
    };

    run();

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        run();
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      stopped = true;
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [uid]);
};
